import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getHandoffPackage, handoffFileUrl, type HandoffPackage } from '@/api/app-handoff';
import { buildSteps, type GuideStep } from './handoff/guideSteps';
import { IconCheck, IconLeft, IconList, IconRight, IconX, Where } from './handoff/HandoffUi';

/**
 * The receiving end of an app handoff: everything Apps Setup produced for one
 * app, walked through in the order the Play Console asks for it, so someone
 * with no account here — and no Play Console experience — can copy the app
 * into their own developer console one step at a time. The token in the URL
 * is the only credential — the API answers 404 for a revoked or expired one,
 * and this page says so in plain words rather than showing an error.
 */
export function AppHandoff() {
  const { token = '' } = useParams<{ token: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['handoff', token],
    queryFn: () => getHandoffPackage(token),
    retry: false,
  });

  // A handoff link must not end up in a search index even if it leaks.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  if (isLoading) {
    return (
      <Shell>
        <div className="space-y-3">
          <div className="h-16 animate-pulse rounded-xl bg-surface" />
          <div className="h-80 animate-pulse rounded-xl bg-surface" />
        </div>
      </Shell>
    );
  }
  if (error || !data) {
    return (
      <Shell>
        <div className="rounded-xl border border-border bg-surface p-6 text-center sm:p-10">
          <h1 className="text-xl font-semibold text-content">This link is no longer valid</h1>
          <p className="mt-2 text-sm text-content-muted">
            Handoff links expire, and they can be revoked at any time. Ask whoever sent it for a new one.
          </p>
        </div>
      </Shell>
    );
  }

  return <Guide token={token} pkg={data} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface-sunken px-4 py-10">
      <div className="mx-auto max-w-3xl">{children}</div>
    </div>
  );
}

function fmtDate(unix: number): string {
  return new Date(unix * 1000).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Progress, kept per link in this browser only. The token never leaves the
// URL it came in: the key holds a prefix, enough to tell two links apart.

type Mode = 'steps' | 'all';
interface Progress { done: string[]; at: string; mode: Mode }

function storageKey(token: string) {
  return `handoff-guide:${token.slice(0, 16)}`;
}

function loadProgress(token: string): Progress {
  try {
    const raw = localStorage.getItem(storageKey(token));
    if (raw) {
      const p = JSON.parse(raw) as Partial<Progress>;
      return {
        done: Array.isArray(p.done) ? p.done.filter((x) => typeof x === 'string') : [],
        at: typeof p.at === 'string' ? p.at : '',
        mode: p.mode === 'all' ? 'all' : 'steps',
      };
    }
  } catch {
    // Private window or blocked storage: the guide works, it just forgets.
  }
  return { done: [], at: '', mode: 'steps' };
}

function useProgress(token: string) {
  const [progress, setProgress] = useState<Progress>(() => loadProgress(token));
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(token), JSON.stringify(progress));
    } catch {
      // see loadProgress
    }
  }, [token, progress]);
  return [progress, setProgress] as const;
}

// ---------------------------------------------------------------------------

function Guide({ token, pkg }: { token: string; pkg: HandoffPackage }) {
  const steps = useMemo(() => buildSteps(token, pkg), [token, pkg]);
  const [progress, setProgress] = useProgress(token);
  const [sheet, setSheet] = useState(false);

  const done = new Set(progress.done);
  const index = Math.max(0, steps.findIndex((s) => s.id === progress.at));
  const step = steps[index]!;
  // The closing "All done" step is not a task of its own.
  const tasks = steps.filter((s) => s.id !== 'done');
  const doneCount = tasks.filter((s) => done.has(s.id)).length;

  function goTo(i: number) {
    const target = steps[Math.min(Math.max(i, 0), steps.length - 1)]!;
    setProgress((p) => ({ ...p, at: target.id }));
    setSheet(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function finishAndNext() {
    setProgress((p) => ({
      ...p,
      done: p.done.includes(step.id) || step.id === 'done' ? p.done : [...p.done, step.id],
    }));
    goTo(index + 1);
  }
  function toggleDone(id: string) {
    setProgress((p) => ({
      ...p,
      done: p.done.includes(id) ? p.done.filter((x) => x !== id) : [...p.done, id],
    }));
  }
  function setMode(mode: Mode) {
    setProgress((p) => ({ ...p, mode }));
    if (mode === 'all') {
      // Land on the step that was open, not on the top of a long page.
      window.setTimeout(() => document.getElementById(`step-${step.id}`)?.scrollIntoView({ block: 'start' }), 0);
    }
  }

  return (
    <div className="min-h-screen bg-surface-sunken pb-28 lg:pb-12">
      <header className="sticky top-0 z-30 border-b border-border bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          {pkg.has_icon
            ? <img src={handoffFileUrl.icon(token)} alt="" className="h-10 w-10 shrink-0 rounded-xl border border-border object-cover" />
            : <span className="h-10 w-10 shrink-0 rounded-xl border border-dashed border-border" />}
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-semibold text-content">{pkg.app.name}</h1>
            <p className="truncate text-xs text-content-subtle">{pkg.app.bundle_id}</p>
          </div>
          <div role="tablist" className="flex shrink-0 rounded-lg bg-surface-muted p-0.5 text-xs font-medium">
            {(['steps', 'all'] as const).map((m) => (
              <button key={m} type="button" role="tab" aria-selected={progress.mode === m} onClick={() => setMode(m)}
                className={`rounded-md px-2.5 py-1.5 transition-colors ${
                  progress.mode === m ? 'bg-surface text-content shadow-sm' : 'text-content-muted hover:text-content'}`}>
                <span className="sm:hidden">{m === 'steps' ? 'Steps' : 'All'}</span>
                <span className="hidden sm:inline">{m === 'steps' ? 'Step by step' : 'All on one page'}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="h-1 bg-surface-muted">
          <div className="h-full bg-success transition-[width] duration-300" style={{ width: `${(doneCount / tasks.length) * 100}%` }} />
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-5 lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:gap-8 lg:py-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-3">
            <p className="text-xs font-medium uppercase tracking-wide text-content-subtle">
              {doneCount} of {tasks.length} done
            </p>
            <StepList steps={steps} current={progress.mode === 'steps' ? step.id : ''} done={done}
              onPick={(i) => (progress.mode === 'steps' ? goTo(i) : document.getElementById(`step-${steps[i]!.id}`)?.scrollIntoView({ behavior: 'smooth' }))} />
            <p className="px-3 text-xs text-content-subtle">Link works until {fmtDate(pkg.expires_at)}.</p>
          </div>
        </aside>

        <main className="min-w-0 space-y-5">
          {progress.mode === 'steps' ? (
            <StepCard step={step} n={index + 1} total={steps.length} done={done.has(step.id)} footerDesktopOnly
              footer={
                <div className="flex items-center justify-between gap-3">
                  <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-4 text-sm font-medium text-content-muted hover:bg-surface-muted hover:text-content disabled:invisible">
                    <IconLeft /> Back
                  </button>
                  {index < steps.length - 1 && (
                    <button type="button" onClick={finishAndNext}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-white hover:bg-primary-600">
                      {step.id === 'start' ? 'Start' : 'Done, next'} <IconRight />
                    </button>
                  )}
                </div>
              } />
          ) : (
            steps.map((s, i) => (
              <StepCard key={s.id} step={s} n={i + 1} total={steps.length} done={done.has(s.id)}
                footer={s.id === 'done' ? null : (
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-content">
                    <input type="checkbox" checked={done.has(s.id)} onChange={() => toggleDone(s.id)}
                      className="h-5 w-5 rounded border-border accent-[#635BFF]" />
                    Done
                  </label>
                )} />
            ))
          )}
          <p className="text-center text-xs text-content-subtle lg:hidden">
            This link works without an account until {fmtDate(pkg.expires_at)}.
          </p>
        </main>
      </div>

      {/* Phone: the step controls sit under the thumb. */}
      {progress.mode === 'steps' && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface/95 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:hidden">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Back"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-border text-content disabled:opacity-30">
              <IconLeft className="h-5 w-5" />
            </button>
            <button type="button" onClick={() => setSheet(true)}
              className="flex h-12 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl border border-border px-3 text-sm text-content">
              <IconList className="h-4 w-4 shrink-0" />
              <span className="truncate">Step {index + 1} of {steps.length}</span>
            </button>
            {index < steps.length - 1 && (
              <button type="button" onClick={finishAndNext}
                className="flex h-12 shrink-0 items-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-semibold text-white">
                {step.id === 'start' ? 'Start' : 'Done'} <IconRight className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {sheet && (
        <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/40 sm:hidden" onClick={() => setSheet(false)}>
          <div className="max-h-[80vh] overflow-y-auto rounded-t-2xl bg-surface p-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
            onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between px-2">
              <p className="text-sm font-semibold text-content">{doneCount} of {tasks.length} done</p>
              <button type="button" onClick={() => setSheet(false)} aria-label="Close"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-content-muted">
                <IconX className="h-5 w-5" />
              </button>
            </div>
            <StepList steps={steps} current={step.id} done={done} onPick={goTo} />
          </div>
        </div>
      )}
    </div>
  );
}

function StepList({ steps, current, done, onPick }: {
  steps: GuideStep[]; current: string; done: Set<string>; onPick: (i: number) => void;
}) {
  return (
    <ol className="space-y-0.5">
      {steps.map((s, i) => {
        const active = s.id === current;
        const isDone = done.has(s.id);
        return (
          <li key={s.id}>
            <button type="button" onClick={() => onPick(i)}
              className={`flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors ${
                active ? 'bg-primary/10 font-medium text-content' : 'text-content-muted hover:bg-surface-muted hover:text-content'}`}>
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                isDone ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-surface-muted text-content-muted'}`}>
                {isDone ? <IconCheck className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={`truncate ${isDone && !active ? 'line-through decoration-content-subtle/50' : ''}`}>{s.title}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function StepCard({ step, n, total, done, footer, footerDesktopOnly }: {
  step: GuideStep; n: number; total: number; done: boolean; footer: React.ReactNode;
  /** The phone has the bottom bar instead. */
  footerDesktopOnly?: boolean;
}) {
  return (
    <section id={`step-${step.id}`} className="scroll-mt-24 overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
      <div className="space-y-2 border-b border-border px-4 py-4 sm:px-6 sm:py-5">
        <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-content-subtle">
          Step {n} of {total}
          {done && (
            <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 normal-case tracking-normal text-success">
              <IconCheck className="h-3 w-3" /> done
            </span>
          )}
        </p>
        <h2 className="text-xl font-semibold text-content sm:text-2xl">{step.title}</h2>
        {step.intro && <p className="text-sm text-content-muted">{step.intro}</p>}
        {step.where && <div className="pt-1"><Where path={step.where} /></div>}
      </div>
      <div className="px-4 py-5 sm:px-6">{step.body}</div>
      {footer && (
        <div className={`border-t border-border px-4 py-3 sm:px-6 ${footerDesktopOnly ? 'hidden sm:block' : ''}`}>{footer}</div>
      )}
    </section>
  );
}

export default AppHandoff;
